import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faArrowRight, faArrowUp, faArrowDown,
  faLocationArrow, faClock, faRoad, faList, faChevronDown,
  faFlag, faMapMarkerAlt, faXmark, faPhone, faCommentDots,
  faCheckCircle, faCircleNotch
} from '@fortawesome/free-solid-svg-icons';
import { QRCodeSVG } from 'qrcode.react';
import { SERVICE_STATUSES } from '../../../constants';
import ProgressBar from '../../UI/ProgressBar';

function getTurnIcon(modifier) {
  switch (modifier) {
    case 'left': return faArrowLeft;
    case 'right': return faArrowRight;
    case 'slight_left': return faArrowLeft;
    case 'slight_right': return faArrowRight;
    case 'sharp_left': return faArrowLeft;
    case 'sharp_right': return faArrowRight;
    case 'straight': return faArrowUp;
    case 'uturn': return faArrowDown;
    default: return faLocationArrow;
  }
}

function getTurnColor(type) {
  if (type === 'arrive') return '#22C55E';
  return 'var(--bry-accent)';
}

export default function NavigationPanel({
  currentStep,
  currentStepIndex,
  steps,
  remainingDistance,
  remainingDuration,
  formatDistance,
  formatDuration,
  service,
  isDriver = true,
  onClose,
  onStatusChange,
  updating = false,
  confirmationToken = null,
  onOpenChat,
  onCancel,
}) {
  const [showSteps, setShowSteps] = useState(false);

  const stepList = Array.isArray(steps) ? steps : [];
  const totalSteps = stepList.length;
  const progress = totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;
  const nextManeuverDistance = currentStep?.distance || 0;

  return (
    <div
      className="navigation-panel"
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: 'var(--bry-header-bg, #111111)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderTopLeftRadius: '24px',
        borderTopRightRadius: '24px',
        borderTop: '1px solid var(--bry-border-subtle, rgba(255,255,255,0.1))',
        boxShadow: '0 -10px 40px rgba(0,0,0,0.7)',
        maxHeight: showSteps ? '70vh' : 'auto',
        overflow: 'hidden',
        transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        color: 'var(--bry-text-primary, #FFFFFF)',
      }}
    >
      {/* Handle pill para deslizar estilo Figma */}
      <div 
        className="pt-3 pb-1 flex justify-center cursor-pointer" 
        onClick={() => setShowSteps(!showSteps)}
      >
        <div 
          className="w-12 h-1.5 rounded-full opacity-60 hover:opacity-100 transition-opacity" 
          style={{ background: 'var(--bry-border-strong, #444444)' }} 
        />
      </div>

      <div className="px-4 pt-2 pb-4">
        {/* Top Header Row */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <span 
              className="fw-bold tracking-wider uppercase" 
              style={{ fontSize: 11, color: 'var(--bry-accent)', letterSpacing: '1px' }}
            >
              Navegación Activa
            </span>
            {nextManeuverDistance > 0 && (
              <span 
                className="badge rounded-pill px-2.5 py-1"
                style={{ background: 'rgba(14,171,176,0.15)', color: 'var(--bry-accent)', fontSize: 11 }}
              >
                en {formatDistance(nextManeuverDistance)}
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            {totalSteps > 0 && (
              <button
                className="bry-btn bry-btn-ghost bry-btn-sm rounded-circle p-2 border-0"
                style={{ background: 'var(--bry-bg-hover, #1a1a1a)', color: 'var(--bry-text-secondary)', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={() => setShowSteps(!showSteps)}
                title="Lista de pasos"
              >
                <FontAwesomeIcon icon={showSteps ? faChevronDown : faList} size="sm" />
              </button>
            )}
            <button
              className="bry-btn bry-btn-ghost bry-btn-sm rounded-circle p-2 border-0"
              style={{ background: 'var(--bry-bg-hover, #1a1a1a)', color: 'var(--bry-text-secondary)', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={onClose}
              title="Cerrar navegación"
            >
              <FontAwesomeIcon icon={faXmark} size="sm" />
            </button>
          </div>
        </div>

        {/* Next Turn Instruction Card */}
        <div 
          className="d-flex align-items-center gap-3 mb-3 p-3 rounded-4 shadow-sm" 
          style={{ background: 'var(--bry-bg-card, #141414)', border: '1px solid var(--bry-border-default, rgba(255,255,255,0.08))' }}
        >
          <div
            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{
              width: 48,
              height: 48,
              background: getTurnColor(currentStep?.type),
              color: 'var(--bry-white, #FFFFFF)',
              fontSize: 20,
              boxShadow: `0 0 16px ${getTurnColor(currentStep?.type)}40`,
            }}
          >
            <FontAwesomeIcon icon={currentStep?.type === 'arrive' ? faFlag : getTurnIcon(currentStep?.modifier)} />
          </div>
          <div className="flex-fill overflow-hidden">
            <div className="fw-bold text-truncate" style={{ fontSize: 16, lineHeight: 1.3, color: 'var(--bry-text-primary, #FFFFFF)' }}>
              {currentStep?.action || 'Sigue la ruta en el mapa'}
            </div>
            {currentStep?.name && (
              <div className="text-truncate" style={{ fontSize: 13, color: 'var(--bry-text-secondary, #A0A0A0)', marginTop: 2 }}>
                {currentStep.name}
              </div>
            )}
          </div>
        </div>

        {/* ETA & Distance Row (Figma 2-Column Summary) */}
        <div className="row g-2 mb-3">
          <div className="col-6">
            <div
              className="d-flex align-items-center gap-2 rounded-3 p-3"
              style={{ background: 'var(--bry-bg-card, #141414)', border: '1px solid var(--bry-border-default, rgba(255,255,255,0.08))' }}
            >
              <FontAwesomeIcon icon={faClock} style={{ color: 'var(--bry-accent)', fontSize: 16 }} />
              <div>
                <div style={{ fontSize: 10, color: 'var(--bry-text-tertiary, #666666)', fontWeight: 700, letterSpacing: '0.5px' }}>TIEMPO ESTIMADO</div>
                <div className="fw-bold" style={{ fontSize: 18, lineHeight: 1.2, color: 'var(--bry-text-primary, #FFFFFF)' }}>
                  {formatDuration(remainingDuration)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-6">
            <div
              className="d-flex align-items-center gap-2 rounded-3 p-3"
              style={{ background: 'var(--bry-bg-card, #141414)', border: '1px solid var(--bry-border-default, rgba(255,255,255,0.08))' }}
            >
              <FontAwesomeIcon icon={faRoad} style={{ color: 'var(--bry-accent)', fontSize: 16 }} />
              <div>
                <div style={{ fontSize: 10, color: 'var(--bry-text-tertiary, #666666)', fontWeight: 700, letterSpacing: '0.5px' }}>DISTANCIA</div>
                <div className="fw-bold" style={{ fontSize: 18, lineHeight: 1.2, color: 'var(--bry-text-primary, #FFFFFF)' }}>
                  {formatDistance(remainingDistance)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Two-Tone Progress Bar (Figma Driving Pattern) */}
        <ProgressBar progress={progress} height="6px" />

        {/* Cockpit Conductor 1-Tap & Info de Viaje */}
        {isDriver && service && (
          <div className="mt-3 pt-2">
            {/* Quick contact and destination chip */}
            <div className="d-flex justify-content-between align-items-center mb-2 px-1">
              <div className="text-truncate me-2" style={{ maxWidth: '70%' }}>
                <span className="text-secondary small d-block" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {service.status === SERVICE_STATUSES.DRIVER_ASSIGNED ? 'Destino de recogida' : 'Destino final'}
                </span>
                <span className="fw-bold text-truncate text-white small d-block">
                  {service.status === SERVICE_STATUSES.DRIVER_ASSIGNED ? (service.pickup?.address || 'Ubicación del cliente') : (service.dropoff?.address || 'Destino')}
                </span>
              </div>
              <div className="d-flex gap-2">
                {onOpenChat && (
                  <button
                    type="button"
                    onClick={onOpenChat}
                    className="btn btn-dark rounded-circle p-2 border border-secondary shadow-sm"
                    style={{ width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Chat con el cliente"
                  >
                    <FontAwesomeIcon icon={faCommentDots} style={{ color: 'var(--bry-accent)' }} />
                  </button>
                )}
                {onCancel && service.status === SERVICE_STATUSES.DRIVER_ASSIGNED && (
                  <button
                    type="button"
                    onClick={onCancel}
                    disabled={updating}
                    className="btn btn-outline-danger rounded-circle p-2 shadow-sm"
                    style={{ width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Cancelar servicio"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
            </div>

            {/* BOTÓN GIGANTE DE ESTADO (1-TAP ACTION) */}
            {onStatusChange && (
              <div className="mt-2">
                {service.status === SERVICE_STATUSES.DRIVER_ASSIGNED && (
                  <button
                    type="button"
                    disabled={updating}
                    onClick={() => onStatusChange(SERVICE_STATUSES.IN_PROGRESS)}
                    className="w-100 py-3 px-3 rounded-xl fw-bold d-flex align-items-center justify-content-center gap-2 border-0 shadow"
                    style={{
                      background: 'linear-gradient(135deg, #0EABB0 0%, #088D91 100%)',
                      color: '#ffffff',
                      fontSize: '1rem',
                      minHeight: '52px',
                      cursor: 'pointer'
                    }}
                  >
                    {updating ? (
                      <FontAwesomeIcon icon={faCircleNotch} spin />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faFlag} />
                        <span>LLEGUÉ / INICIAR VIAJE</span>
                      </>
                    )}
                  </button>
                )}

                {service.status === SERVICE_STATUSES.IN_PROGRESS && (
                  <button
                    type="button"
                    disabled={updating}
                    onClick={() => onStatusChange(SERVICE_STATUSES.AWAITING_CONFIRMATION)}
                    className="w-100 py-3 px-3 rounded-xl fw-bold d-flex align-items-center justify-content-center gap-2 border-0 shadow"
                    style={{
                      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                      color: '#ffffff',
                      fontSize: '1.05rem',
                      minHeight: '52px',
                      boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)',
                      cursor: 'pointer'
                    }}
                  >
                    {updating ? (
                      <FontAwesomeIcon icon={faCircleNotch} spin />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <span>COMPLETAR SERVICIO</span>
                      </>
                    )}
                  </button>
                )}

                {service.status === SERVICE_STATUSES.AWAITING_CONFIRMATION && (
                  <div className="p-3 rounded-3 text-center border mt-2" style={{ background: '#1c1917', borderColor: '#444' }}>
                    <div className="text-white small fw-bold mb-2">
                      Muestra este código QR al cliente para finalizar
                    </div>
                    {confirmationToken && (
                      <div className="d-inline-block p-2 bg-white rounded shadow-sm">
                        <QRCodeSVG
                          value={`${window.location.origin}/confirmar/${confirmationToken}`}
                          size={130}
                          level="M"
                          includeMargin
                        />
                      </div>
                    )}
                    <div className="text-muted small mt-2" style={{ fontSize: 11 }}>
                      Esperando que el cliente escanee o confirme...
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expandable Step-by-Step Instructions */}
      {showSteps && stepList.length > 0 && (
        <div
          className="px-4 pb-4"
          style={{ maxHeight: '42vh', overflowY: 'auto' }}
        >
          <div className="my-2" style={{ borderTop: '1px solid var(--bry-border-subtle, rgba(255,255,255,0.08))' }} />
          <div className="fw-bold mb-2 uppercase" style={{ fontSize: 11, color: 'var(--bry-text-tertiary, #666666)', letterSpacing: '0.5px' }}>
            Instrucciones de Ruta ({totalSteps} pasos)
          </div>
          <div className="d-flex flex-column gap-2">
            {stepList.map((step, i) => (
              <div
                key={i}
                className="d-flex align-items-center gap-3 p-2.5 rounded-3"
                style={{
                  background: i === currentStepIndex ? 'rgba(14,171,176,0.1)' : 'var(--bry-bg-card, #141414)',
                  borderLeft: i === currentStepIndex ? '3px solid var(--bry-accent)' : '3px solid transparent',
                  border: i === currentStepIndex ? '1px solid rgba(14,171,176,0.3)' : '1px solid var(--bry-border-subtle, rgba(255,255,255,0.06))',
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                  style={{
                    width: 30,
                    height: 30,
                    background: i === currentStepIndex ? 'var(--bry-accent)' : 'var(--bry-border-strong, #333333)',
                    color: i === currentStepIndex ? '#000000' : 'var(--bry-text-secondary, #A0A0A0)',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  <FontAwesomeIcon icon={getTurnIcon(step.modifier)} />
                </div>
                <div className="flex-fill" style={{ minWidth: 0 }}>
                  <div className="fw-bold text-truncate" style={{ fontSize: 13, lineHeight: 1.2, color: 'var(--bry-text-primary, #FFFFFF)' }}>
                    {step.action}
                  </div>
                  {step.name && (
                    <div className="text-truncate" style={{ fontSize: 11, color: 'var(--bry-text-secondary, #A0A0A0)' }}>
                      {step.name}
                    </div>
                  )}
                </div>
                <div className="text-end flex-shrink-0" style={{ fontSize: 11, color: 'var(--bry-text-secondary, #A0A0A0)', whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {formatDistance(step.distance)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

